import { OrgTypes } from '@mode/shared/contract-common';
import { compact, intersectionWith, isEqual } from 'lodash';

import { OrganizationHelpers } from '@mode/legacy/data-access-model-js';
import {
  OrganizationFeatureResource,
  OrganizationFeatures,
  OrganizationResource,
} from '@mode/legacy/data-access-model-js';
import {
  AppStateFeatureFlagState,
  createFlag,
  FeatureFlagStateType,
  OrganizationFeatureFlagState,
  PlanFeatureFlagState,
} from '@mode/legacy/types-app-state';
import { AppStateFeatures, FEATURE_FLAG_MAP } from '@mode/legacy/types-app-state';

type StructuredAppFeatureState = {
  [K in AppStateFeatures.Type.SelectedReport]: boolean;
};

export namespace FeatureFlagHelpers {
  export function flagsIntersect(
    featureBindings: FeatureFlagStateType[],
    whatsEnabled: FeatureFlagStateType[]
  ): boolean {
    return intersectionWith(whatsEnabled, featureBindings, isEqual).length > 0;
  }

  export function featureIsEnabled(whatsEnabled: FeatureFlagStateType[], featureName: string): boolean | null {
    const flags = [...FEATURE_FLAG_MAP.get(featureName)];

    return flagsIntersect(flags, whatsEnabled);
  }

  export function orgFeaturesToFeatureFlags(
    state: OrganizationFeatureResource,
    context
  ): OrganizationFeatureFlagState[] {
    const result: OrganizationFeatureFlagState[] = [];

    for (const feature in state) {
      if (state.hasOwnProperty(feature) && state[feature] === true) {
        result.push(createFlag({ context, type: <OrganizationFeatures.Type>feature }));
      }
    }

    return result;
  }

  export function orgPlanCodeToFeatureFlag(
    state: OrganizationResource,
    context: OrgTypes.PlanCodeContext
  ): PlanFeatureFlagState {
    if (state) {
      if (state.hasOwnProperty('plan_code')) {
        return createFlag({ context, type: state.plan_code });
      } else {
        // community accounts don't have a plan code and they are considered standard
        return createFlag({ context, type: OrgTypes.PlanCode.Standard });
      }
    }
  }

  export function appStatesToFeatureFlags(state: StructuredAppFeatureState): AppStateFeatureFlagState[] {
    const result: AppStateFeatureFlagState[] = [];

    for (const feature in state) {
      if (state.hasOwnProperty(feature) && state[feature] === true) {
        result.push(createFlag({ context: null, type: <AppStateFeatures.Types>feature }));
      }
    }

    return result;
  }

  export function orgToFeatureFlags(account: OrganizationResource, owner: OrganizationResource) {
    return compact([
      FeatureFlagHelpers.orgPlanCodeToFeatureFlag(owner, OrgTypes.PlanCodeContext.Report),
      FeatureFlagHelpers.orgPlanCodeToFeatureFlag(account, OrgTypes.PlanCodeContext.User),
      ...FeatureFlagHelpers.orgFeaturesToFeatureFlags(
        OrganizationHelpers.getOrgFeatures(owner),
        OrganizationFeatures.Context.Report
      ),
      ...FeatureFlagHelpers.orgFeaturesToFeatureFlags(
        OrganizationHelpers.getOrgFeatures(account),
        OrganizationFeatures.Context.User
      ),
    ]);
  }
}
